import {fetchListPazirandeBankAccounts,} from "@/api/merchant/request.get";
import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import {CircularProgress, InputAdornment, TextField} from "@mui/material";
import {useMutation, useQuery} from "@tanstack/react-query";
import React, {useEffect, useState} from "react";
import {mapBankTitleToImageName} from "./data";
import StackedImageSlider from "@/shared/components/common/slider";
import {toEnglishNumber, toPersianNumber,} from "@/hooks/useThousandsSeparator";
import {fetchCreatePazirandeBank} from "@/api/merchant/request.post";
import {AxiosError} from "axios";
import {toast} from "react-toastify";

/**
 * BankCardFormSection
 *
 * فرم واکنش‌گرای ثبت مشخصات کارت بانکی و نمایش کارت‌های کاربر.
 * در دستگاه‌های کوچک اجزا به صورت ستونی و در دسکتاپ به صورت دو ستونه نمایش داده می‌شوند.
 *
 * @component
 * @returns JSX.Element
 */
const Content = () => {
    const [cardNumber, setCardNumber] = useState("");
    const [cardNumberSheba, setCardNumberSheba] = useState("");

    const {data: dataListPazirandeClubs, refetch} = useQuery({
        queryKey: ["ListPazirandeClubscardNumber-data"],
        queryFn: fetchListPazirandeBankAccounts,
    });

    useEffect(() => {
        refetch();
    }, []);

    const bankAccounts = dataListPazirandeClubs ?? [];

    const itemsImg = bankAccounts?.map((item) => {
        const imageName = mapBankTitleToImageName(item.title);
        return {
            id: item.id,
            url: `/bank-cards/${imageName}.jpg`,
            title: item.title,
            footer: item.card,
            status: item.status,
        };
    });

    const {mutate: submitPazirandeDashboardChart, isPending: isPendingLogin} =
        useMutation({
            mutationFn: fetchCreatePazirandeBank,
            onSuccess: (data: any) => {
                toast.success("کارت با موفقیت ثبت شد");
            },
            onError: (error: AxiosError<{ message: string }>) => {
                toast.error(error?.response?.data?.message || "خطا در ثبت اطلاعات");
            },
        });

    const handleCardNumberSheba = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCardNumberSheba(e.target.value);
    };

    const handleClick = () => {
        const requestBody = {
            card_number: cardNumber,
            iban: cardNumberSheba,
        };
        submitPazirandeDashboardChart(requestBody);
    };

    return (
        <StackKit direction={{xs: "column-reverse", md: "row"}}
                  sx={{width: "100%", alignItems: {xs: "stretch", md: "center"}, justifyContent: "space-between",}}
                  gap={{xs: 3, md: 4}}>
            {/* Left Side – Form Section */}
            <StackKit direction="column" sx={{
                width: {xs: "100%", md: "60%"},
                minHeight: {xs: "auto", md: "456px"},
                background: "#434343",
                justifyContent: "space-around",
                borderRadius: "8px",
                p: {xs: 2, md: 3},
            }} gap={2}>
                <BoxKit sx={{width: "100%", display: "flex", justifyContent: "start"}}>
                    <TypoGraphyKit variant="body1" sx={{textAlign: "right"}}>
                        مشخصات کارت بانکی خود را وارد نمایید. شماره کارت وارد شده پس از احراز
                        هویت در پنل شما ثبت میگردد.
                    </TypoGraphyKit>
                </BoxKit>
                <StackKit sx={{width: "100%"}} justifyContent="center" direction="row">
                    <BoxKit
                        sx={{
                            width: {xs: "100%", sm: "280px", md: "305px"},
                            height: {xs: "150px", md: "180px"},
                            border: "1px solid",
                            display: "flex",
                            justifyContent: "center",
                            borderRadius: "8px",
                            alignItems: "center",
                            borderImageSource: "linear-gradient(116.58deg, #1F1F1F -21.78%, #3D3D3D 184.44%)",
                            bgcolor: "#1A1A1A",
                        }}
                    >
                        <img src="/logoincontent-pullipay.svg" alt="logo" style={{width: "80%", height: "auto"}}/>
                    </BoxKit>
                </StackKit>
                <StackKit direction={{xs: "column", sm: "row"}} sx={{width: "100%"}} justifyContent="space-between"
                          alignItems={{xs: "flex-start", sm: "center"}} gap={{xs: 1.5, sm: 0}}>
                    <BoxKit>
                        <TypoGraphyKit variant="body1">
                            شماره کارت بانکی خود را وارد نمایید
                        </TypoGraphyKit>
                    </BoxKit>
                    <TextField
                        placeholder={'شماره کارت'}
                        sx={{width: {xs: "100%", sm: "50%"}, direction: "ltr"}}
                        value={cardNumber ? toPersianNumber(cardNumber) : ""}
                        onChange={(e) => {
                            const englishNumber = toEnglishNumber(e.target.value);
                            const rawValue = englishNumber.replace(/[^0-9]/g, "");
                            if (rawValue.length <= 16) setCardNumber(rawValue);
                        }}
                    />
                </StackKit>
                <StackKit direction={{xs: "column", sm: "row"}} sx={{width: "100%"}} justifyContent="space-between"
                          alignItems={{xs: "flex-start", sm: "center"}} gap={{xs: 1.5, sm: 0}}>
                    <BoxKit sx={{textAlign: "right"}}>
                        <TypoGraphyKit variant="body1">
                            شماره شبا کارت بانکی را وارد نمایید
                        </TypoGraphyKit>
                    </BoxKit>
                    <TextField
                        placeholder={'شماره شبا'}
                        sx={{width: {xs: "100%", sm: "51%"}, direction: "ltr"}}
                        value={cardNumberSheba ? toPersianNumber(cardNumberSheba) : ""}
                        onChange={(e) => {
                            const englishNumber = toEnglishNumber(e.target.value);
                            const rawValue = englishNumber.replace(/[^0-9]/g, "");
                            if (rawValue.length <= 24) setCardNumberSheba(rawValue);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">IR</InputAdornment>
                            ),
                        }}
                    />
                </StackKit>
                <StackKit direction="row" sx={{width: "100%"}} justifyContent="end">
                    <CustomButton sx={{width: {xs: "100%", sm: "fit-content"}, height: "48px", mt: {xs: 2, sm: 0},}}
                                  onClick={handleClick}>
                        {isPendingLogin ? (
                            <CircularProgress sx={{color: "#fff"}} size={24}/>
                        ) : (
                            <TypoGraphyKit variant="body1">تایید و ادامه</TypoGraphyKit>
                        )}
                    </CustomButton>
                </StackKit>
            </StackKit>

            {/* Right Side – Card Preview Section */}
            <StackKit sx={{width: {xs: "100%", md: "40%"}, mt: {xs: 3, md: 0}, alignItems: "center",}}>
                {dataListPazirandeClubs && dataListPazirandeClubs?.length > 0 ? (
                    <StackKit direction="column" sx={{width: {xs: "80%", sm: "60%", md: "50%"}}} alignItems="end">
                        <StackedImageSlider
                            items={itemsImg}
                            renderItem={(item) => (
                                <div style={{position: "relative", width: "100%", height: "100%",}}>
                                    <img src={item.url} alt="کارت"
                                         onError={(e) => ((e.target as HTMLImageElement).src = "/bank-cards/default.jpg")}
                                         style={{
                                             width: "100%",
                                             height: "100%",
                                             objectFit: "cover",
                                             borderRadius: "12px",
                                             filter: item.status === "pending" ? "grayscale(100%)" : "none",
                                         }}/>
                                    <div style={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                                        color: "white",
                                        padding: "8px 12px",
                                        borderRadius: "8px",
                                        fontSize: "14px",
                                    }}>
                                        {item.title}
                                    </div>
                                    <div style={{
                                        position: "absolute",
                                        bottom: "12px",
                                        right: "12px",
                                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                                        color: "#333",
                                        padding: "6px 10px",
                                        borderRadius: "6px",
                                        fontSize: "12px",
                                    }}>
                                        {item.footer}
                                    </div>
                                </div>
                            )}
                        />
                    </StackKit>
                ) : (
                    <StackKit sx={{
                        bgcolor: "#2E2E2E",
                        width: {xs: "100%", sm: "283px"},
                        height: {xs: "140px", sm: "160px"},
                        borderRadius: "8px",
                    }} justifyContent="center" alignItems="center">
                        <TypoGraphyKit variant="body1">کارت بانکی وجود ندارد</TypoGraphyKit>
                    </StackKit>
                )}
            </StackKit>
        </StackKit>

    );
};

export default Content;
